// src/utils/linkUtils.jsx
// Helpers for detecting and presenting pasted URLs inside free-text content
// (press releases, article bodies, descriptions) so links get real attention.
//
// Detection is deliberately forgiving — press-release text routinely contains:
//   • proper URLs:      https://example.org/path
//   • bare domains:     communitycharitableministry.org
//   • typo'd schemes:   https//xooponline.com/donations  (missing colon)
//   • www-prefixed:     www.example.com
// ...while avoiding false positives on prose ("i.e.", "June 8, 2025") and on
// the domain portion of email addresses ("jane@charity.org").
import React from 'react';
import { ExternalLink } from 'lucide-react';

// Common TLDs we accept for *bare* domains (no scheme). Kept reasonably tight
// so prose like "8, 2025" or "i.e." isn't mistaken for a link.
const TLD = '(?:com|org|net|edu|gov|mil|int|io|co|us|uk|ca|info|biz|me|tv|app|dev|online|store|shop|ngo|charity|foundation|community)';

// Scheme (colon optional, to tolerate the "https//" typo) OR www. OR bare domain.
// Built with plain concatenation (not template literals) to keep regex escapes unambiguous.
const SCHEME_PART = 'https?:?\\/\\/[^\\s<]+';
const WWW_PART = 'www\\.[^\\s<]+';
const BARE_PART = '[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\\.[a-z0-9-]+)*\\.' + TLD + '(?:\\/[^\\s<]*)?';
const URL_PATTERN = '(?:' + SCHEME_PART + '|' + WWW_PART + '|' + BARE_PART + ')';

const TRAILING_PUNCT = /[.,;:!?)\]}'"]+$/;

const buildRegex = () => new RegExp(URL_PATTERN, 'gi');

// Strip trailing punctuation a greedy match may have swallowed
// (e.g. "see x.org." -> ["x.org", "."]).
const splitTrailingPunct = (token) => {
    const match = token.match(TRAILING_PUNCT);
    if (!match) return [token, ''];
    return [token.slice(0, -match[0].length), match[0]];
};

// Turn any detected token into a valid, clickable href:
//   "https//x.com"  -> "https://x.com"   (repair missing colon)
//   "example.org"   -> "https://example.org"
//   "www.x.com"     -> "https://www.x.com"
export const normalizeUrl = (raw) => {
    const url = raw.trim().replace(/^(https?)(\/\/)/i, '$1://'); // repair "https//"
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
};

// A short, human-friendly label: drop the scheme/"www." and truncate long paths.
export const prettifyUrl = (url) => {
    const label = url
        .replace(/^https?:?\/\//i, '')
        .replace(/^www\./i, '')
        .replace(/\/$/, '');
    return label.length > 42 ? `${label.slice(0, 42)}…` : label;
};

// Break text into an ordered list of { type: 'text' | 'link' } tokens.
// A match immediately preceded by "@" is treated as an email domain and left
// as plain text (no lookbehind used, for broad browser support).
const tokenize = (text) => {
    const tokens = [];
    const regex = buildRegex();
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        const start = match.index;
        const raw = match[0];

        if (text[start - 1] === '@') {
            continue; // email domain — leave for the surrounding text slice
        }
        if (start > lastIndex) {
            tokens.push({ type: 'text', value: text.slice(lastIndex, start) });
        }
        const [url, trailing] = splitTrailingPunct(raw);
        tokens.push({ type: 'link', value: url });
        if (trailing) {
            tokens.push({ type: 'text', value: trailing });
        }
        lastIndex = start + raw.length;
    }
    if (lastIndex < text.length) {
        tokens.push({ type: 'text', value: text.slice(lastIndex) });
    }
    return tokens;
};

// All links found in a block of text, normalized and de-duplicated, in order.
export const extractLinks = (text) => {
    if (!text) return [];
    const urls = tokenize(text)
        .filter((t) => t.type === 'link')
        .map((t) => normalizeUrl(t.value));
    return [...new Set(urls)];
};

// A clean preview string with raw URLs removed — used for clamped card text so
// long links don't crowd out the actual message.
export const getPreviewText = (text) => {
    if (!text) return '';
    return tokenize(text)
        .filter((t) => t.type === 'text')
        .map((t) => t.value)
        .join('')
        .replace(/\s+/g, ' ')
        .trim();
};

// Render free text as React nodes, turning every URL into a highlighted,
// clickable inline chip that stands out from the surrounding prose.
export const renderTextWithLinks = (text) => {
    if (!text) return null;
    return tokenize(text).map((token, index) => {
        if (token.type === 'text') {
            return <React.Fragment key={index}>{token.value}</React.Fragment>;
        }
        return (
            <a
                key={index}
                href={normalizeUrl(token.value)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="mx-0.5 inline-flex max-w-full items-center gap-1 rounded-md bg-purple-50 px-1.5 py-0.5
                         align-baseline font-semibold text-purple-700 underline decoration-purple-300 underline-offset-2
                         transition-colors hover:bg-purple-100 hover:text-purple-800 break-all"
            >
                <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                {prettifyUrl(normalizeUrl(token.value))}
            </a>
        );
    });
};
